import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Label,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { hedgesCompanyApi, type Hedge } from "@/services/api";
import {
  Shield,
  ArrowLeft,
  Loader2,
  FileText,
  Calendar,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertTriangle,
  Wallet,
  RefreshCw,
  Download,
  FileCheck,
} from "lucide-react";
import { formatDate, cn } from "@/lib/utils";

const statusConfig: Record<
  string,
  { label: string; variant: "default" | "secondary" | "success" | "warning" | "destructive"; description: string }
> = {
  PENDING_GUARANTEE: { label: "Aguardando Garantia", variant: "warning", description: "Deposite a garantia para ativar a proteção" },
  PENDING_APPROVAL: { label: "Aguardando Aprovação", variant: "warning", description: "Sua proteção está em análise pela equipe" },
  ACTIVE: { label: "Ativo", variant: "success", description: "Sua taxa está travada e protegida" },
  PENDING_SETTLEMENT: { label: "Aguardando Liquidação", variant: "warning", description: "Vencimento próximo - liquide sua proteção" },
  SETTLED: { label: "Liquidado", variant: "secondary", description: "Proteção liquidada com sucesso" },
  REJECTED: { label: "Rejeitado", variant: "destructive", description: "Proteção rejeitada pela equipe" },
  DEFAULTED: { label: "Inadimplente", variant: "destructive", description: "Proteção em inadimplência" },
  CANCELLED: { label: "Cancelado", variant: "secondary", description: "Proteção foi cancelada" },
};

function formatUsdc(value: string | number | undefined): string {
  if (!value) return "$0.00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(num);
}

function formatBrl(value: string | number | undefined): string {
  if (!value) return "R$ 0,00";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(num);
}

export function DetalheHedgePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [hedge, setHedge] = useState<Hedge | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloadingTerm, setDownloadingTerm] = useState(false);

  const loadHedge = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await hedgesCompanyApi.getHedge(id);
      setHedge(data);
    } catch (error) {
      console.error("Erro ao carregar hedge:", error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadHedge();
  }, [loadHedge]);

  const handleDownloadTerm = async () => {
    if (!id || !hedge?.termAcceptedAt) return;

    try {
      setDownloadingTerm(true);
      const blob = await hedgesCompanyApi.getTermPdf(id);

      // Criar link de download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `termo-hedge-${hedge.hedgeCode}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar termo:", error);
    } finally {
      setDownloadingTerm(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hedge) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertTriangle className="w-12 h-12 text-muted-foreground" />
        <p className="text-muted-foreground">Hedge não encontrado</p>
        <Button onClick={() => navigate("/protecao-cambial")}>
          Voltar para Lista
        </Button>
      </div>
    );
  }

  const status = statusConfig[hedge.status] || { label: hedge.status, variant: "secondary" as const, description: "" };
  const daysUntilDue = Math.ceil(
    (new Date(hedge.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
  const isNearDue = daysUntilDue <= 7 && daysUntilDue > 0;
  const isOverdue = daysUntilDue < 0;

  // Calcular economia/custo comparado com taxa atual (mockado)
  const currentRate = 5.50; // Seria obtido de API
  const lockedRate = parseFloat(hedge.lockedExchangeRate || "0");
  const hedgeAmount = parseFloat(hedge.hedgeAmountUsdc || "0");
  const savings = (currentRate - lockedRate) * hedgeAmount;
  const isSaving = savings > 0;

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Shield className="h-5 w-5 sm:h-6 sm:w-6" />}
        title={`Proteção ${hedge.hedgeCode}`}
        description={status.description}
        actions={
          <div className="flex gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={loadHedge}
              disabled={loading}
            >
              <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
              Atualizar
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={() => navigate("/protecao-cambial")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
        }
      />

      {/* Status Banner */}
      <Card className={cn(
        "border-l-4",
        status.variant === "success" && "border-l-green-500 bg-green-50/30",
        status.variant === "warning" && "border-l-orange-500 bg-orange-50/30",
        status.variant === "destructive" && "border-l-red-500 bg-red-50/30",
        status.variant === "secondary" && "border-l-gray-500 bg-gray-50/30"
      )}>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge variant={status.variant} className="text-sm">
                {status.label}
              </Badge>
              {isNearDue && (
                <span className="flex items-center gap-1 text-orange-600 text-sm">
                  <Clock className="h-4 w-4" />
                  Vence em {daysUntilDue} dia{daysUntilDue !== 1 ? "s" : ""}
                </span>
              )}
              {isOverdue && (
                <span className="flex items-center gap-1 text-red-600 text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  Vencido há {Math.abs(daysUntilDue)} dia{Math.abs(daysUntilDue) !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {/* Ações baseadas no status */}
            {hedge.status === "PENDING_GUARANTEE" && (
              <Button onClick={() => navigate(`/protecao-cambial/${id}/garantia`)}>
                <Wallet className="h-4 w-4 mr-2" />
                Depositar Garantia
              </Button>
            )}
            {(hedge.status === "ACTIVE" || hedge.status === "PENDING_SETTLEMENT") && (
              <Button onClick={() => navigate(`/protecao-cambial/${id}/liquidar`)}>
                <TrendingUp className="h-4 w-4 mr-2" />
                Liquidar Agora
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Grid de Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Comparativo de Taxa (se ativo) */}
        {(hedge.status === "ACTIVE" || hedge.status === "PENDING_SETTLEMENT") && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Comparativo de Taxa
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                <div>
                  <Label className="text-muted-foreground">Taxa Travada</Label>
                  <p className="text-2xl font-bold text-green-600">
                    R$ {lockedRate.toFixed(4)}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Taxa Atual do Mercado</Label>
                  <p className="text-2xl font-bold">
                    R$ {currentRate.toFixed(4)}
                  </p>
                  <p className="text-xs text-muted-foreground">* Taxa ilustrativa</p>
                </div>
                <div className={cn(
                  "rounded-lg p-4",
                  isSaving ? "bg-green-100" : "bg-orange-100"
                )}>
                  <Label className={isSaving ? "text-green-800" : "text-orange-800"}>
                    {isSaving ? "Economia Estimada" : "Custo de Proteção"}
                  </Label>
                  <p className={cn(
                    "text-2xl font-bold",
                    isSaving ? "text-green-700" : "text-orange-700"
                  )}>
                    {formatBrl(Math.abs(savings))}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Valores */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Valores
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-muted-foreground">Valor do Hedge</Label>
                <p className="text-xl font-bold">{formatUsdc(hedge.hedgeAmountUsdc)}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Valor Total Travado (BRL)</Label>
                <p className="text-xl font-bold">{formatBrl(hedge.lockedAmountBrl)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Valor fixo em BRL para o hedge de {formatUsdc(hedge.hedgeAmountUsdc)}
                </p>
              </div>
            </div>

            <div className="border-t pt-4">
              <div>
                <Label className="text-muted-foreground">Garantia ({hedge.guaranteePercentage}%)</Label>
                <p>{formatUsdc(hedge.guaranteeAmountUsdc)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Colateral bloqueado em USDC, devolvido na liquidação
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Taxas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Taxas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-muted-foreground">Câmbio de Referência</Label>
                <p className="text-lg">R$ {parseFloat(hedge.spotExchangeRate || "0").toFixed(4)}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Taxa Travada</Label>
                <p className="text-lg font-bold text-green-600">
                  R$ {parseFloat(hedge.lockedExchangeRate || "0").toFixed(4)}
                </p>
              </div>
            </div>

            <div className="border-t pt-4 space-y-2">
              <div>
                <Label className="text-muted-foreground">Taxa de Proteção</Label>
                <p className="text-lg font-semibold">{formatBrl(hedge.protectionFeeBrl || hedge.spreadAmountBrl)}</p>
              </div>
            </div>

            {hedge.settlementSpotRate && (
              <div className="border-t pt-4">
                <Label className="text-muted-foreground">Taxa na Liquidação</Label>
                <p>R$ {parseFloat(hedge.settlementSpotRate).toFixed(4)}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoice */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Invoice
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-muted-foreground">Número</Label>
                <p className="font-medium">{hedge.invoice?.invoiceNumber || "-"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Fornecedor</Label>
                <p className="font-medium">{hedge.invoice?.supplierName || "-"}</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label className="text-muted-foreground">País</Label>
                  <p>{hedge.invoice?.supplierCountry || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Valor Original</Label>
                  <p>{formatUsdc(hedge.invoice?.amountUsdc)}</p>
                </div>
              </div>
            </div>

            {hedge.invoice?.description && (
              <div className="border-t pt-4">
                <Label className="text-muted-foreground">Descrição</Label>
                <p className="text-sm mt-1">{hedge.invoice.description}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Datas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Datas
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-muted-foreground">Criação</Label>
                <p>{formatDate(hedge.createdAt)}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Vencimento</Label>
                <p className={cn(
                  "font-medium",
                  isOverdue && "text-red-600",
                  isNearDue && "text-orange-600"
                )}>
                  {formatDate(hedge.dueDate)}
                </p>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {hedge.guaranteeDepositedAt && (
                  <div>
                    <Label className="text-muted-foreground">Garantia Depositada</Label>
                    <p>{formatDate(hedge.guaranteeDepositedAt)}</p>
                  </div>
                )}
                {hedge.approvedAt && (
                  <div>
                    <Label className="text-muted-foreground">Aprovado em</Label>
                    <p>{formatDate(hedge.approvedAt)}</p>
                  </div>
                )}
                {hedge.settledAt && (
                  <div>
                    <Label className="text-muted-foreground">Liquidado em</Label>
                    <p>{formatDate(hedge.settledAt)}</p>
                  </div>
                )}
                {hedge.gracePeriodEndDate && (
                  <div>
                    <Label className="text-muted-foreground">Fim da Carência</Label>
                    <p>{formatDate(hedge.gracePeriodEndDate)}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Termo de Proteção */}
        {hedge.termAcceptedAt && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-green-600" />
                Termo de Proteção Cambial
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    <span className="font-medium">Termo aceito</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Aceito em {formatDate(hedge.termAcceptedAt)}
                  </p>
                  {hedge.termHash && (
                    <p className="text-xs text-muted-foreground font-mono break-all">
                      Hash SHA-256: {hedge.termHash}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  onClick={handleDownloadTerm}
                  disabled={downloadingTerm}
                >
                  {downloadingTerm ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Baixando...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Baixar Termo PDF
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Timeline (simplificada) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Histórico
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <TimelineItem
              date={hedge.createdAt}
              title="Hedge criado"
              description={`Valor: ${formatUsdc(hedge.hedgeAmountUsdc)} | Taxa: R$ ${parseFloat(hedge.lockedExchangeRate || "0").toFixed(4)}`}
              completed
            />

            {hedge.guaranteeDepositedAt && (
              <TimelineItem
                date={hedge.guaranteeDepositedAt}
                title="Garantia depositada"
                description={formatUsdc(hedge.guaranteeAmountUsdc)}
                completed
              />
            )}

            {hedge.approvedAt && (
              <TimelineItem
                date={hedge.approvedAt}
                title="Hedge aprovado"
                description="Aprovado pela equipe"
                completed
              />
            )}

            {hedge.status === "PENDING_GUARANTEE" && (
              <TimelineItem
                title="Aguardando garantia"
                description="Deposite a garantia para continuar"
                pending
              />
            )}

            {hedge.status === "PENDING_APPROVAL" && (
              <TimelineItem
                title="Em análise"
                description="Aguardando aprovação da equipe"
                pending
              />
            )}

            {hedge.settledAt ? (
              <TimelineItem
                date={hedge.settledAt}
                title="Hedge liquidado"
                description="Proteção concluída com sucesso"
                completed
              />
            ) : hedge.status === "ACTIVE" || hedge.status === "PENDING_SETTLEMENT" ? (
              <TimelineItem
                date={hedge.dueDate}
                title="Vencimento"
                description="Data prevista para liquidação"
                pending
              />
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Componente de item da timeline
function TimelineItem({
  date,
  title,
  description,
  completed,
  pending,
}: {
  date?: string;
  title: string;
  description: string;
  completed?: boolean;
  pending?: boolean;
}) {
  return (
    <div className="flex items-start gap-4">
      <div className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
        completed && "bg-green-100",
        pending && "bg-orange-100",
        !completed && !pending && "bg-muted"
      )}>
        {completed ? (
          <CheckCircle className="w-4 h-4 text-green-600" />
        ) : pending ? (
          <Clock className="w-4 h-4 text-orange-600" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-muted-foreground" />
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium">{title}</p>
          {date && (
            <span className="text-sm text-muted-foreground">
              {formatDate(date)}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
